import React, { Dispatch, SetStateAction, useCallback, useRef } from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { Portal } from "reakit/Portal";
import { ITeam, ResGetTeams, TacticApiClient } from "@gettactic/api";
import { classNames } from "@gettactic/helpers/src/css/classNames";
import Link from "next/link";
import { Checkbox } from "@cmp-src/primitives/checkbox";

type Props = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  hasAnyFav: boolean;
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  setArrowElement: Dispatch<SetStateAction<HTMLElement | null>>;
  styles: { [p: string]: React.CSSProperties };
  attributes: { [p: string]: { [p: string]: string } | undefined };
  referenceElement: HTMLElement | null;
  addUserToDefaultGroup: (api: TacticApiClient) => void;
  removeUserFromDefaultGroup: (api: TacticApiClient) => void;
  addUserToGroup: (p: ITeam, api: TacticApiClient) => void;
  removeUserFromGroup: (p: ITeam, api: TacticApiClient) => void;
  groups: undefined | ResGetTeams;
  userGroups: undefined | ResGetTeams;
  api: TacticApiClient;
};

export function FavsDetails({
  setOpen,
  setPopperElement,
  setArrowElement,
  open,
  styles,
  attributes,
  referenceElement,
  hasAnyFav,
  addUserToDefaultGroup,
  removeUserFromDefaultGroup,
  addUserToGroup,
  removeUserFromGroup,
  groups,
  userGroups,
  api
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const refToOpen = useRef(referenceElement);
  refToOpen.current = referenceElement;
  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  useOnclickOutside(close, {
    refs: [ref as never, refToOpen as never]
  });

  return (
    <Portal>
      <div
        className={classNames(!open ? "hidden" : "")}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div
          className={classNames(
            !open ? "hidden opacity-0" : " opacity-100 m-2 bg-white",
            "rounded-lg border bg-white shadow-lg transition duration-300 ease-in-out"
          )}
        >
          <div
            ref={setArrowElement}
            style={styles.arrow}
            {...attributes.arrow}
          />
          <div className="my-2" ref={ref}>
            <h4 className="border-b border-neutral-200 text-base leading-relaxed text-neutral-900 pb-2.5 ">
              <span className="px-3 text-sm text-neutral-900">Add to...</span>
            </h4>
            <ul className="mt-3 px-2 ">
              {!groups || groups.elements.length === 0 ? (
                <li className="flex items-center space-x-2 text-sm">
                  <Checkbox
                    checked={hasAnyFav}
                    onCheckedChange={() => {
                      if (hasAnyFav) {
                        removeUserFromDefaultGroup(api);
                      } else {
                        addUserToDefaultGroup(api);
                      }
                    }}
                  />
                  <span>My Favorites</span>
                </li>
              ) : (
                groups.elements.map((group) => {
                  const isInGroup =
                    !!userGroups &&
                    !!userGroups.elements.find((x) => x.id === group.id);
                  return (
                    <li
                      key={group.id}
                      className="flex items-center space-x-2 text-sm"
                    >
                      <Checkbox
                        checked={isInGroup}
                        onCheckedChange={async () => {
                          if (isInGroup) {
                            await removeUserFromGroup(group, api);
                          } else {
                            await addUserToGroup(group, api);
                          }
                        }}
                      />
                      <span>{group.name}</span>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="link mt-4 px-2 text-neutral-600 text-sm underline">
              <Link className="" href="/settings/favorites">
                Visit My Favorites Lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
